import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { RecordingConsentStatus } from '../ambient/recording-consent-types';

/**
 * PROD-3195. The ambient-recording consent check for an encounter, run in the background when the
 * encounter opens (with the panel load).
 *
 *   GET /api/Consent/AmbientRecording/Status?encounterId= -> AmbientConsentEvaluation
 *
 * Contract: Scrips.Patient `ConsentController.GetAmbientRecordingStatus` (PROD-2683). `[Authorize]`
 * only; the organisation comes from the OrganizationId header the api client already sends. It
 * always answers 200: `status` is "Granted" or "Denied", and a denial carries a `reasonCode`. The
 * body holds no PHI (ids, the minor flag, the signer role, dates).
 *
 * UX only. Video enforces consent at session start. On dev, `AmbientScribe:Consent:Required=false`
 * bypasses consent in Video but not here, so this can disable Start where the server would allow it.
 * The UI follows this endpoint (Andrew).
 */
export interface AmbientConsentEvaluation {
  status: 'Granted' | 'Denied' | string;
  reasonCode?: string | null;
  isMinor?: boolean;
  /** "patient" (adult) or "guardian" (minor). */
  requiredSignerRole?: string | null;
  consentRecordId?: string | null;
  signedAt?: string | null;
  expiresAt?: string | null;
}

export const recordingConsentKey = (encounterId?: string | null) =>
  ['ambient-recording-consent', encounterId] as const;

export function toRecordingConsentStatus(evaluation: AmbientConsentEvaluation | undefined): RecordingConsentStatus {
  const status = evaluation?.status?.toLowerCase();
  if (status === 'granted') return 'granted';
  if (status === 'denied') return 'denied';
  return 'unknown';
}

export interface RecordingConsentCheck {
  status: RecordingConsentStatus;
  isMinor: boolean;
  requiredSignerRole: 'patient' | 'guardian' | null;
  reasonCode: string | null;
}

/** Whether the last answer was an explicit denial (the only state that refetches on focus or reconnect). */
export function isDeniedAnswer(evaluation: AmbientConsentEvaluation | undefined): boolean {
  return toRecordingConsentStatus(evaluation) === 'denied';
}

export function useRecordingConsentStatus(encounterId: string | null | undefined): RecordingConsentCheck {
  const query = useQuery<AmbientConsentEvaluation>({
    queryKey: recordingConsentKey(encounterId),
    enabled: !!encounterId,
    // A signature changes the answer without the encounter changing (the sign flow invalidates this
    // key), and a clinician may also sign in another tab: ask again when the window regains focus.
    queryFn: ({ signal }) =>
      get<AmbientConsentEvaluation>(
        `/api/Consent/AmbientRecording/Status?encounterId=${encodeURIComponent(encounterId as string)}`,
        { signal },
      ),
    retry: 1,
    // Consent is checked only at the start of a session (Samer, 20 Oct 2026). Once the answer is
    // GRANTED nothing asks again by itself (no focus or reconnect refetch), so a running recording
    // never triggers a consent request. While DENIED, a focus or reconnect still asks again, to pick
    // up a signature made in another tab before Start; and so does a check that FAILED, so the
    // pre-Start state does not stick on an error.
    refetchOnWindowFocus: (q) => q.state.status === 'error' || isDeniedAnswer(q.state.data),
    refetchOnReconnect: (q) => q.state.status === 'error' || isDeniedAnswer(q.state.data),
  });

  let status: RecordingConsentStatus;
  if (!encounterId) status = 'unknown'; // no encounter: the panel already fails closed on its own
  else if (query.isError) status = 'unknown'; // fail open: the server decides at Start
  else if (query.data) status = toRecordingConsentStatus(query.data);
  else status = 'checking';

  const role = query.data?.requiredSignerRole?.toLowerCase();
  return {
    status,
    isMinor: !!query.data?.isMinor,
    requiredSignerRole: role === 'guardian' ? 'guardian' : role === 'patient' ? 'patient' : null,
    reasonCode: query.data?.reasonCode ?? null,
  };
}

/**
 * PROD-3195. Who signs the consent now: the guardian on file for a minor, the patient for an adult.
 * The server names the role (`requiredSignerRole`); `isMinor` is the fail-closed backup when it did not.
 */
export function signerForRecordingConsent(check: Pick<RecordingConsentCheck, 'isMinor' | 'requiredSignerRole'>): 'patient' | 'guardian' {
  if (check.requiredSignerRole) return check.requiredSignerRole;
  return check.isMinor ? 'guardian' : 'patient';
}
